import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Modal, Form, Input, Select, Row, Col, message } from 'antd';
import { addVendor, updateVendor } from '@/api/vendors';
import type { Vendor } from '@/types/resources/vendor';
import { useAuth } from '@/contexts/AuthContext';

interface VendorModalProps {
  open: boolean;
  vendor: Vendor | null;
  onSuccess: () => void;
  onCancel: () => void;
}

export const VendorModal: React.FC<VendorModalProps> = ({
  open,
  vendor,
  onSuccess,
  onCancel,
}) => {
  const { t } = useTranslation(['vendors', 'common']);
  const queryClient = useQueryClient();
  const [form] = Form.useForm<Partial<Vendor>>();
  const { firestoreUser } = useAuth();

  // Mutations
  const addMutation = useMutation({
    mutationFn: (values: Omit<Vendor, 'id'>) => {
      if (!firestoreUser?.workspace) {
        throw new Error('No workspace found');
      }
      return addVendor(
        {
          ...values,
          type: values.type || 'supplier',
          status: values.status || 'active'
        },
        firestoreUser
      );
    },
    onSuccess: () => {
      message.success(t('vendors:messages.addSuccess'));
      queryClient.invalidateQueries({ queryKey: ['vendors'] });
      onSuccess();
    },
    onError: (error) => {
      message.error(t('vendors:messages.addError'));
      console.error('Error adding vendor:', error);
    }
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<Vendor> }) => {
      if (!firestoreUser) {
        throw new Error('No authenticated user found');
      }
      return updateVendor(id, data, firestoreUser);
    },
    onSuccess: (_, variables) => {
      message.success(t('vendors:messages.updateSuccess'));
      // Invalidate both the list and the specific vendor
      queryClient.invalidateQueries({ queryKey: ['vendors'] });
      queryClient.invalidateQueries({ queryKey: ['vendor', variables.id] });
      onSuccess();
    },
    onError: (error) => {
      message.error(t('vendors:messages.updateError'));
      console.error('Error updating vendor:', error);
    }
  });

  // Reset form when modal opens/closes or vendor changes
  useEffect(() => {
    if (open) {
      if (vendor) {
        form.setFieldsValue(vendor);
      } else {
        form.resetFields();
        // Set default values for new vendor
        form.setFieldsValue({
          type: 'supplier',
          status: 'active'
        });
      }
    }
  }, [open, vendor, form]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      
      if (vendor) {
        updateMutation.mutate({ id: vendor.id, data: values });
      } else {
        addMutation.mutate(values as Omit<Vendor, 'id'>);
      }
    } catch (error) {
      console.error('Form validation failed:', error);
    }
  };

  return (
    <Modal
      title={vendor ? t('vendors:actions.edit') : t('vendors:actions.add')}
      open={open}
      onOk={handleSubmit}
      onCancel={onCancel}
      confirmLoading={addMutation.isPending || updateMutation.isPending}
      okText={t('common:actions.save')}
      cancelText={t('common:actions.cancel')}
      width={800}
    >
      <Form
        form={form}
        layout="vertical"
        name="vendorForm"
      >
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="name"
              label={t('vendors:fields.name')}
              rules={[{ required: true, message: t('common:fieldRequired') }]}
            >
              <Input placeholder={t('vendors:fields.namePlaceholder')} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="type"
              label={t('vendors:fields.type')}
              rules={[{ required: true, message: t('common:fieldRequired') }]}
            >
              <Select placeholder={t('vendors:fields.typePlaceholder')}>
                {Object.entries(t('vendors:types', { returnObjects: true }) as [string, string][]).map(([key, value]) => (
                  <Select.Option key={key} value={key}>{value}</Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="category"
              label={t('vendors:fields.category')}
              rules={[{ required: true, message: t('common:fieldRequired') }]}
            >
              <Select placeholder={t('vendors:fields.categoryPlaceholder')}>
                {Object.entries(t('vendors:categories', { returnObjects: true }) as [string, string][]).map(([key, value]) => (
                  <Select.Option key={key} value={key}>{value}</Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="status"
              label={t('vendors:fields.status')}
              rules={[{ required: true, message: t('common:fieldRequired') }]}
            >
              <Select placeholder={t('vendors:fields.statusPlaceholder')}>
                {Object.entries(t('vendors:status', { returnObjects: true }) as [string, string][]).map(([key, value]) => (
                  <Select.Option key={key} value={key}>{value}</Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="contactPerson"
              label={t('vendors:fields.contactPerson')}
              rules={[{ required: true, message: t('common:fieldRequired') }]}
            >
              <Input placeholder={t('vendors:fields.contactPersonPlaceholder')} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="email"
              label={t('vendors:fields.email')}
              rules={[
                { required: true, message: t('common:fieldRequired') },
                { type: 'email', message: t('common:invalidEmail') }
              ]}
            >
              <Input placeholder={t('vendors:fields.emailPlaceholder')} />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="phone"
              label={t('vendors:fields.phone')}
              rules={[{ required: true, message: t('common:fieldRequired') }]}
            >
              <Input placeholder={t('vendors:fields.phonePlaceholder')} />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item
          name="address"
          label={t('vendors:fields.address')}
          rules={[{ required: true, message: t('common:fieldRequired') }]}
        >
          <Input.TextArea rows={3} placeholder={t('vendors:fields.addressPlaceholder')} />
        </Form.Item>

        <Form.Item
          name="taxId"
          label={t('vendors:fields.taxId')}
        >
          <Input placeholder={t('vendors:fields.taxIdPlaceholder')} />
        </Form.Item>

        <Form.Item
          name="notes"
          label={t('vendors:fields.notes')}
        >
          <Input.TextArea rows={3} placeholder={t('vendors:fields.notesPlaceholder')} />
        </Form.Item>
      </Form>
    </Modal>
  );
}; 